import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { Box, Button, CircularProgress, List, ListItem, TextField, Typography } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '../../../../shared/lib/common/Alert';
import { BUTTON_TEXT_CANCEL, LABEL_DEACTIVATE_ACCOUNT } from '../../../../shared/lib/constants';
import palette from '../../../../shared/lib/theme/palette';
import { LINKS } from '../../app/constants';
import { useAppContext } from '../../app/contexts/AppContext';
import { ActionType } from '../../app/reducer/AppContextReducer';
import { handleLogout } from '../../app/utils';
import { useDeactivateUserAccountMutation } from '../../generated';
import { CustomModal } from '../common/CustomModal';

export const DeActiveAccountModal = () => {
  const { dispatch, isOpen, currentUser, setIsAppUserLoggedIn, setCurrentUser } = useAppContext();
  const { fullName, email } = currentUser || {};
  const { blackTwo, borderLight } = palette;
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const handleClose = () => {
    dispatch({ type: ActionType.SET_IS_OPEN, isOpen: false });
    setMessage('');
  };

  const [deactivate, { loading }] = useDeactivateUserAccountMutation({
    onError({ message }) {
      Alert.error(message);
    },

    async onCompleted(data) {
      const { deactivateUserAccount } = data;

      if (deactivateUserAccount) {
        setMessage('');
        setCurrentUser(null);
        await handleLogout();
        setIsAppUserLoggedIn(false);
        navigate(LINKS.LOGIN_LINK);
      }
    }
  });
  const handleDeactivate = async () => {
    await deactivate();
    handleClose();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  };

  return (
    <CustomModal maxWidth="500px" isOpen={isOpen} handleClose={handleClose}>
      <Box padding="0 8px">
        <Box position="relative">
          <Box padding="24px 0">
            <Box display="flex" alignItems="center" mb="5px">
              <ErrorOutlineIcon color="error" sx={{ mr: 1 }} />

              <Typography variant="h6" sx={{ fontWeight: '700', color: 'blackOne', marginTop: '4px' }}>
                {LABEL_DEACTIVATE_ACCOUNT}
              </Typography>
            </Box>

            <Typography variant="h2" color={blackTwo}>
              Are you sure you want to deactivate your account?
            </Typography>
          </Box>

          <Box my={1} mb={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: '700', mb: 1 }}>
              {fullName}
            </Typography>
            <Typography variant="subtitle2">{email}</Typography>
          </Box>

          <Typography color="blackOne" sx={{ display: 'flex', alignItems: 'center' }}>
            <ReportProblemIcon fontSize="small" color="error" sx={{ mr: 1 }} />
            This is an irreversible action and will:
          </Typography>

          <List sx={{ listStyleType: 'disc', listStylePosition: 'inside', border: '0', paddingLeft: '10px' }}>
            <ListItem disablePadding sx={{ display: 'list-item', fontSize: '14px' }}>
              Delete your Trusted Contacts.
            </ListItem>
            <ListItem disablePadding sx={{ display: 'list-item', fontSize: '14px' }}>
              Delete your subscription.
            </ListItem>
            <ListItem disablePadding sx={{ display: 'list-item', fontSize: '14px' }}>
              Delete your phone number and phone verification.
            </ListItem>
            <ListItem disablePadding sx={{ display: 'list-item', fontSize: '14px' }}>
              Delete your T-Pin.
            </ListItem>
          </List>

          <Typography sx={{ fontSize: '12px' }}>
            <Typography sx={{ mr: '5px', fontSize: '12px', fontWeight: 500 }} component="span">
              Note:
            </Typography>
            This will deactivate your account. You can contact support for re-activation any time in the future.
          </Typography>

          <Box mb={1} mt={1}>
            <Typography component="span" variant="subtitle2" mb={2}>{`Please type "`}</Typography>
            <Typography component="span" variant="subtitle2" mb={2} sx={{ fontWeight: 600 }}>
              {LABEL_DEACTIVATE_ACCOUNT}
            </Typography>
            <Typography pb={2} component="span" variant="subtitle2">
              {`" to continue:`}
            </Typography>
          </Box>

          <TextField fullWidth type="text" id="message" name="message" onChange={handleChange} value={message} />

          <Box display="flex" width="100%" gap="10px" mt="24px" mb="8px">
            <Button fullWidth onClick={handleClose} variant="text" sx={{ border: `1px solid ${borderLight}` }}>
              {BUTTON_TEXT_CANCEL}
            </Button>

            <Button fullWidth onClick={handleDeactivate} variant="contained" color="error" disabled={loading || message !== 'DEACTIVATE'} endIcon={loading && <CircularProgress size={20} />}>
              Deactivate
            </Button>
          </Box>
        </Box>
      </Box>
    </CustomModal>
  );
};
